import React, { useState, useEffect, useCallback } from 'react';
import { FiFileText, FiDownload, FiTrash2, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import api from '../services/api';
import './Admin.css';

function AdminSpinRecordPage({ campaignId }) {
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({});
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const loadRecords = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const params = { page, pageSize: 20 };
      if (search) params.search = search;
      if (filter) params.spinResult = filter;
      const data = await api.getSpinRecords(campaignId, params);
      setRecords(data.records || []);
      setPagination(data.pagination || {});
      setSelectedIds([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [campaignId, page, search, filter]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const handleExport = async () => {
    try {
      const blob = await api.exportSpinRecords(campaignId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `spin_records_${campaignId}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('匯出失敗');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadRecords();
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('確定刪除此抽獎紀錄？')) return;
    try {
      await api.deleteSpinRecord(recordId);
      loadRecords();
    } catch (err) {
      alert(err.message || '刪除失敗');
    }
  };

  const isAllSelected = records.length > 0 && selectedIds.length === records.length;
  const isIndeterminate = selectedIds.length > 0 && selectedIds.length < records.length;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(records.map(r => r.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`確定刪除已選取的 ${selectedIds.length} 筆抽獎紀錄？`)) return;
    try {
      await api.bulkDeleteSpinRecords(selectedIds);
      loadRecords();
    } catch (err) {
      alert(err.message || '批次刪除失敗');
    }
  };

  if (!campaignId) return <div className="admin-empty">請先選擇活動</div>;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title"><FiFileText style={{ marginRight: 8, verticalAlign: 'middle' }} />抽獎紀錄</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {selectedIds.length > 0 && (
            <button className="admin-btn-sm danger" onClick={handleBulkDelete} style={{ padding: '6px 14px', fontSize: 14 }}>
              <FiTrash2 style={{ marginRight: 5, verticalAlign: 'middle' }} />
              刪除已選 ({selectedIds.length})
            </button>
          )}
          <button className="admin-btn-secondary" onClick={handleExport}>
            <FiDownload style={{ marginRight: 6, verticalAlign: 'middle' }} />匯出 CSV
          </button>
        </div>
      </div>

      {/* 搜尋列 */}
      <form className="admin-search-bar" onSubmit={handleSearch}>
        <input
          type="text"
          placeholder="搜尋姓名 / 手機 / 身分證..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }}>
          <option value="">全部結果</option>
          <option value="win">中獎</option>
          <option value="lose">未中獎</option>
        </select>
        <button type="submit" className="admin-btn-primary">搜尋</button>
      </form>

      {/* 紀錄表格 */}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: 36, textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={el => { if (el) el.indeterminate = isIndeterminate; }}
                  onChange={handleSelectAll}
                  title="全選"
                />
              </th>
              <th>#</th>
              <th>玩家</th>
              <th>身分證字號</th>
              <th>手機</th>
              <th>獎項</th>
              <th>結果</th>
              <th>兌獎狀態</th>
              <th>抽獎時間</th>
              <th>IP</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="11" className="admin-loading">載入中...</td></tr>
            ) : records.length === 0 ? (
              <tr><td colSpan="11" className="admin-loading">尚無紀錄</td></tr>
            ) : records.map(r => (
              <tr key={r.id} className={selectedIds.includes(r.id) ? 'row-selected' : ''}>
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(r.id)}
                    onChange={() => handleSelectOne(r.id)}
                  />
                </td>
                <td>{r.id}</td>
                <td>
                  <strong>{r.player_name}</strong>
                </td>
                <td>{r.player_national_id}</td>
                <td>{r.player_phone}</td>
                <td>
                  {r.prize_icon} {r.prize_display_name || r.prize_name_snapshot}
                </td>
                <td>
                  <span className={`status-badge ${r.spin_result === 'win' ? 'win' : 'lose'}`}>
                    {r.spin_result === 'win' ? <><FiCheckCircle style={{ marginRight: 4, verticalAlign: 'middle' }} />中獎</> : <><FiXCircle style={{ marginRight: 4, verticalAlign: 'middle' }} />未中獎</>}
                  </span>
                </td>
                <td>
                  <span className={`status-badge claim-${r.claim_status}`}>
                    {r.claim_status === 'claimed' ? '已兌獎' :
                     r.claim_status === 'expired' ? '已過期' : '待兌獎'}
                  </span>
                </td>
                <td className="td-time">
                  {new Date(r.created_at).toLocaleString('zh-TW')}
                </td>
                <td className="td-ip">{r.ip}</td>
                <td className="td-actions">
                  <button className="admin-btn-sm danger" onClick={() => handleDelete(r.id)}>
                    <FiTrash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 分頁 */}
      {pagination.totalPages > 1 && (
        <div className="admin-pagination">
          <button
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            上一頁
          </button>
          <span className="page-info">
            第 {pagination.page} / {pagination.totalPages} 頁
            （共 {pagination.total} 筆）
          </span>
          <button
            disabled={page >= pagination.totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            下一頁
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminSpinRecordPage;
